import type { ChannelMessage } from '@aws-sdk/client-chime-sdk-messaging';
import * as D from 'io-ts/Decoder';
import cloneDeep from 'lodash-es/cloneDeep';
import isEmpty from 'lodash-es/isEmpty';
import pick from 'lodash-es/pick';
import uniq from 'lodash-es/uniq';

import { isLeft, type Maybe } from '@bedc/core';
import { decoders } from '@bedc/decoders';
import type { types } from '@bedc/types';

import { timeUtils } from './timeUtils';

// Constants
// ----------------------------------------------------------------------------

// NOTE: The UUID version/variant nibbles are intentionally left unconstrained
// ([0-9a-f] rather than [1-5]/[89ab]) so these patterns match every UUID our ID
// generator produces. `randomUUID` (@bedc/core) emits UUIDv7 (version nibble `7`),
// which the old [1-5] version class silently rejected, so mentions of any
// recently-created user were dropped and no notification was raised. This mirrors
// the canonical version-agnostic matcher in libs/shared/core/src/io-ts/uuid.ts.

/** Regex matching patterns. */
const REGEX_LINK_USER =
	/(@user:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})|(@all)/g;

/** Regex for matching user links in HTML format */
const REGEX_HTML_USER_LINK =
	/data-user-link="(user:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"/g;

// Methods
// ----------------------------------------------------------------------------

/**
 * The channel id is the last ARN segment.
 *
 * Extracted so every producer of a channel id agrees — the analytics ledger keys
 * chat events on it from a different code path than the one that parses messages.
 */
const getChannelIdFromArn = (channelArn: string): string =>
	channelArn.substring(channelArn.lastIndexOf('/') + 1);

/**
 * Convert an individual AWS Chime message into our message.
 *
 * @param channelMessage The AWS Chime message.
 * @param channelArn ARN of the channel this message are for.
 * @returns Our message, or null if unsupported (missing data, other types).
 */
const parseChatMessage = (
	channelMessage:
		| (Omit<
				ChannelMessage,
				'CreatedTimestamp' | 'LastUpdatedTimestamp' | 'LastEditedTimestamp'
		  > & {
				CreatedTimestamp?: Date | string;
				LastUpdatedTimestamp?: Date | string;
				LastEditedTimestamp?: Date | string;
		  })
		| undefined,
	channelArn: string
): types.chat.message.Type | null => {
	// The Chime object has everything as optional, but we require it.
	if (
		!channelMessage ||
		!channelMessage.MessageId ||
		!channelMessage.CreatedTimestamp ||
		!channelMessage.Sender?.Name
	) {
		return null;
	}

	// We currently do not support system/control messages.
	if (channelMessage?.Type !== 'STANDARD') {
		return null;
	}

	let metadata: types.chat.message.Metadata | undefined = undefined;

	// Parse any metadata on the message.
	if (channelMessage?.Metadata) {
		try {
			// Parse the JSON string to an object before decoding if it's a string
			const parsedMetadata =
				typeof channelMessage.Metadata === 'string'
					? JSON.parse(channelMessage.Metadata)
					: channelMessage.Metadata;

			const decoderResult = decoders.chat.message.Metadata.decode(parsedMetadata);

			if (isLeft(decoderResult)) {
				console.error(`Invalid metadata format: ${D.draw(decoderResult.left)}`);
			} else {
				metadata = decoderResult.right;
			}
		} catch (parseError) {
			console.error(`Failed to parse metadata JSON: ${channelMessage.Metadata}`, parseError);
		}
	}

	const quotedMessage = channelMessage.MessageAttributes?.['quotedMessage']?.StringValues?.[0]
		? (JSON.parse(
				channelMessage.MessageAttributes['quotedMessage'].StringValues[0]
			) as types.chat.message.QuotedMessage)
		: undefined;

	// Handle redacted messages
	if (channelMessage.Redacted) {
		return {
			id: channelMessage.MessageId,
			clientId: channelMessage.MessageAttributes?.['id']?.StringValues?.[0],

			channelId: getChannelIdFromArn(channelArn),
			channelArn,

			text: undefined,

			deleted: true,
			lastEditedAt: undefined,

			createdAt:
				timeUtils.safeParseDate(channelMessage.CreatedTimestamp)?.toISOString() ??
				new Date().toISOString(),
			createdBy: channelMessage.Sender.Name,

			metadata: undefined,

			quotedMessage: undefined,
		};
	}

	return {
		id: channelMessage.MessageId,
		clientId: channelMessage.MessageAttributes?.['id']?.StringValues?.[0],

		channelId: getChannelIdFromArn(channelArn),
		channelArn,

		text: channelMessage.Content,

		deleted: channelMessage.Redacted,
		lastEditedAt: timeUtils.safeParseDate(channelMessage.LastEditedTimestamp)?.toISOString(),

		createdAt:
			timeUtils.safeParseDate(channelMessage.CreatedTimestamp)?.toISOString() ??
			new Date().toISOString(),
		createdBy: channelMessage.Sender.Name,

		metadata,

		quotedMessage,
	};
};

/**
 * Check if a user has reacted with a specific emoji in the message metadata.
 *
 * @param metadata The message metadata.
 * @param userId The ID of the user.
 * @param emoji The emoji to check.
 * @returns True if the user has reacted with the emoji, false otherwise.
 */
const userHasReactedWithEmoji = (
	metadata: types.chat.message.Metadata,
	userId: types.user.Id,
	emoji: string
) => {
	const reactions = metadata.reactions || {};
	const userReactions = reactions[userId] || [];
	return userReactions.includes(emoji);
};

/**
 * Toggle an emoji reaction in the message metadata.
 *
 * @param userId The ID of the user reacting.
 * @param emoji The emoji to toggle.
 * @param metadata The message metadata.

 * @returns The updated metadata with the toggled reaction.
 */
const toggleEmojiReactionsInMetadata = (
	userId: types.user.Id,
	emojis: string[],
	metadata?: types.chat.message.Metadata
): Maybe<types.chat.message.Metadata> => {
	const reactions = metadata?.reactions || {};
	const userReactions = reactions[userId] || [];

	// Toggle the reaction - remove if exists, add if not
	const updatedReactions: types.chat.message.Reactions = { ...reactions };

	const updatedUserReactions = [...userReactions];

	for (const emoji of emojis) {
		if (updatedUserReactions.includes(emoji)) {
			// User has already reacted with this emoji, so remove it
			updatedUserReactions.splice(updatedUserReactions.indexOf(emoji), 1);
		} else {
			// User has not reacted with this emoji, so add it
			updatedUserReactions.push(emoji);
		}
	}

	// Add the updated user reactions to the message reactions
	updatedReactions[userId] = updatedUserReactions;

	if (updatedUserReactions.length === 0 && userId in updatedReactions) {
		// User no longer has any reactions, so remove them from the message reactions
		delete updatedReactions[userId];
	}

	// Update metadata with new reactions
	const updatedMetadata: types.chat.message.Metadata = {
		...metadata,
		reactions: updatedReactions,
	};

	if (Object.entries(updatedMetadata?.reactions || {}).length === 0) {
		// If there are no reactions left, remove the reactions property
		delete updatedMetadata.reactions;
	}

	return isEmpty(updatedMetadata) ? undefined : updatedMetadata;
};

/**
 * Compresses a string using the specified compression format and returns it as a base64 string.
 * @param text - The string to compress
 * @param encoding - The compression format to use (defaults to 'gzip')
 * @returns A Promise that resolves to the compressed string in base64 format
 */
const compressMessageContent = async (
	text: string,
	encoding = 'gzip' as CompressionFormat
): Promise<string> => {
	const originalBytes = new TextEncoder().encode(text);

	if (typeof window !== 'undefined') {
		// Browser environment
		const cs = new CompressionStream(encoding);
		const writer = cs.writable.getWriter();
		writer.write(originalBytes);
		writer.close();
		const arrayBuffer = await new Response(cs.readable).arrayBuffer();
		const compressed = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
		return compressed;
	} else {
		// Node.js environment
		const zlib = await import('zlib');
		const util = await import('util');
		const gzipAsync = util.promisify(zlib.gzip);

		const compressed = await gzipAsync(originalBytes);
		return Buffer.from(compressed).toString('base64');
	}
};

/**
 * Decompresses a base64 string using the specified compression format.
 * If the input is not base64 encoded or decompression fails, the original string will be returned unchanged.
 *
 * @param base64String - The base64 encoded compressed string
 * @param encoding - The compression format used (defaults to 'gzip')
 * @returns A Promise that resolves to the decompressed string, or the original string if decompression is not possible
 */
const decompressMessageContent = async (
	base64String: string,
	encoding = 'gzip' as CompressionFormat
): Promise<string> => {
	if (!isBase64(base64String)) {
		return base64String; // If not base64, return as is
	}

	try {
		if (typeof window !== 'undefined') {
			// Browser environment
			const binaryString = atob(base64String);
			const compressedBytes = new Uint8Array(binaryString.length);
			for (let i = 0; i < binaryString.length; i++) {
				compressedBytes[i] = binaryString.charCodeAt(i);
			}

			const cs = new DecompressionStream(encoding);
			const writer = cs.writable.getWriter();
			writer.write(compressedBytes);
			writer.close();
			const arrayBuffer = await new Response(cs.readable).arrayBuffer();
			return new TextDecoder().decode(arrayBuffer);
		} else {
			// Node.js environment
			const zlib = await import('zlib');
			const util = await import('util');
			const gunzipAsync = util.promisify(zlib.gunzip);

			const compressed = Buffer.from(base64String, 'base64');
			const decompressed = await gunzipAsync(compressed);
			return new TextDecoder().decode(decompressed);
		}
	} catch {
		return base64String; // If decompression fails, return the original base64 string
	}
};

/**
 * Checks if a string is valid base64 encoded.
 * @param str - The string to check
 * @returns True if the string is valid base64, false otherwise
 */
const isBase64 = (str?: string): boolean => {
	try {
		// Check if the string matches base64 pattern
		const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
		if (!str || !base64Regex.test(str)) return false;

		if (typeof window !== 'undefined') {
			// Browser environment
			atob(str);
		} else {
			// Node.js environment
			Buffer.from(str, 'base64');
		}
		return true;
	} catch {
		return false;
	}
};

/**
 * Checks if a given channel should use Rome chat APIs.
 * This is determined by whether the channel is either read-only or a stub.
 */
const isCacheEnabledForChannel = (
	channel: Partial<Pick<types.chat.channel.LookupItemOrAbove, 'isReadOnly' | 'isStub'>>
) => !!(channel?.isReadOnly || channel?.isStub);

/**
 * Parse a chat message and return all unique user links within.
 * Supports both old plain text format (@user:uuid) and new HTML format (data-user-link="user:uuid").
 *
 * @param content The raw string, which may be plain text or HTML.
 * @returns The list of unique user IDs found in the content.
 */
export const getLinkedUserIdsInChatMessage = (content: string): string[] => {
	const userIds: string[] = [];

	// Check for plain text format (@user:uuid)
	const plainTextMatches = content
		.split(REGEX_LINK_USER)
		.filter((text) => text?.match(REGEX_LINK_USER))
		.map((text) => text.replace('@', '')); // Remove the link code.
	userIds.push(...plainTextMatches);

	// Check for HTML format (data-user-link="user:uuid")
	const htmlMatches = Array.from(content.matchAll(REGEX_HTML_USER_LINK))
		.map((match) => match[1])
		.filter((value) => value !== undefined); // Extract the user ID from the capture group
	userIds.push(...htmlMatches);

	// Return unique user IDs
	return uniq(userIds);
};

/**
 * Create the attributes object to include in the message.
 *
 * @param id The ID of the message.
 * @param options The options object containing:
 * - quotedMessage: An optional object containing a quoted message to be saved as a snapshot on the message attributes.
 * @returns The attributes object.
 */
export const createChatMessageAttributes = (
	id: string,
	options?: {
		quotedMessage?: types.chat.message.Type;
	}
) => {
	const attributes: Record<string, string | string[]> = {};

	// Include the client-side ID.
	attributes.id = id;

	if (options?.quotedMessage) {
		attributes.quotedMessage = JSON.stringify(
			pick(options.quotedMessage, ['createdAt', 'createdBy', 'text'])
		);
	}

	return attributes;
};

/**
 * Update an existing metadata object with new information.
 *
 * @param currentMetadata The current metadata object.
 * @param userIdLinks Any user links entered in the message text.
 * @param membershipUserIds IDs of all possible users that can be linked.
 * @param deletedFileIds IDs of file attachments that have been deleted.
 * @returns The updated metadata object.
 */
export const updateChatMessageMetadata = ({
	currentMetadata = {},
	userIdLinks,
	membershipUserIds = [],
	deletedFileIds,
}: {
	currentMetadata?: types.chat.message.Metadata;
	userIdLinks?: string[];
	membershipUserIds: types.user.Id[];
	deletedFileIds?: string[];
}): Maybe<types.chat.message.Metadata> => {
	// Start with a copy of the current metadata.
	const newMetadata = cloneDeep(currentMetadata);

	// Update any links.
	newMetadata.links = userIdLinks?.length
		? ({
				users: userIdLinks.includes('all') ? membershipUserIds : userIdLinks,
			} satisfies types.chat.message.Links)
		: undefined;

	// Remove any attachments being deleted.
	if (!!deletedFileIds?.length && newMetadata.attachments) {
		newMetadata.attachments = newMetadata.attachments.filter(
			(a) => !deletedFileIds.includes(a.id)
		);
	}

	return isEmpty(newMetadata) ? undefined : newMetadata;
};

export const chatUtils = {
	getChannelIdFromArn,
	compressMessageContent,
	decompressMessageContent,
	parseChatMessage,
	toggleEmojiReactionsInMetadata,
	userHasReactedWithEmoji,
	isCacheEnabledForChannel,
};
